import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';

// Base selectors
export const wordlistsSelector = (state: RootState) => state.wordlists;

// User wordlists selectors
export const userWordlistsStateSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.userWordlists
);

export const userWordlistsDataSelector = createSelector(
  [userWordlistsStateSelector],
  userWordlists => userWordlists.data
);

export const userWordlistsStatusSelector = createSelector(
  [userWordlistsStateSelector],
  userWordlists => userWordlists.status
);

export const isUserWordlistsLoadingSelector = createSelector(
  [userWordlistsStatusSelector],
  status => status === 'pending'
);

// User wordlists collection selectors
export const userWordlistsCollectionStateSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.userWordlistsCollection
);

export const userWordlistsCollectionDataSelector = createSelector(
  [userWordlistsCollectionStateSelector],
  userWordlistsCollection => userWordlistsCollection.data
);

export const userWordlistsCollectionStatusSelector = createSelector(
  [userWordlistsCollectionStateSelector],
  userWordlistsCollection => userWordlistsCollection.status
);

export const isUserWordlistsCollectionLoadingSelector = createSelector(
  [userWordlistsCollectionStatusSelector],
  status => status === 'pending'
);

// Marketplace wordlists selectors
export const marketplaceWordlistsStateSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.marketplaceWordlists
);

export const marketplaceWordlistsDataSelector = createSelector(
  [marketplaceWordlistsStateSelector],
  marketplaceWordlists => marketplaceWordlists.data
);

export const marketplaceWordlistsStatusSelector = createSelector(
  [marketplaceWordlistsStateSelector],
  marketplaceWordlists => marketplaceWordlists.status
);

export const isMarketplaceWordlistsLoadingSelector = createSelector(
  [marketplaceWordlistsStatusSelector],
  status => status === 'pending'
);

// Wordlist detail selectors
export const wordlistDetailStateSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.wordlistDetail
);

export const wordlistDetailDataSelector = createSelector(
  [wordlistDetailStateSelector],
  wordlistDetail => wordlistDetail.data
);

export const wordlistDetailStatusSelector = createSelector(
  [wordlistDetailStateSelector],
  wordlistDetail => wordlistDetail.status
);

export const isWordlistDetailLoadingSelector = createSelector(
  [wordlistDetailStatusSelector],
  status => status === 'pending'
);

// Selected wordlist ID selector
export const selectedWordlistIdSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.selectedWordlistId
);

// Combined selected wordlist selector (from detail, user lists, collection, or marketplace)
export const selectedWordlistSelector = createSelector(
  [selectedWordlistIdSelector, userWordlistsDataSelector, userWordlistsCollectionDataSelector, marketplaceWordlistsDataSelector, wordlistDetailDataSelector, wordlistDetailStatusSelector],
  (selectedId, userWordlists, collectionWordlists, marketplaceWordlists, detailData, detailStatus) => {
    if (!selectedId) return null;

        // Prefer detail data as it has the complete wordlist with all words
    if (detailData && detailData.id === selectedId) {
      return detailData;
    }

        // If detail is loading, try to return a cached version from the lists to avoid showing stale data
    if (detailStatus === 'pending') {
      const cached = userWordlists.find(w => w.id === selectedId)
                || collectionWordlists.find(w => w.id === selectedId)
                || marketplaceWordlists.find(w => w.id === selectedId);
      return cached || null;
    }

        // Fall back to finding in user wordlists, collection, or marketplace
    return userWordlists.find(w => w.id === selectedId)
            || collectionWordlists.find(w => w.id === selectedId)
            || marketplaceWordlists.find(w => w.id === selectedId)
            || null;
  }
);

/**
 * "My lists" — everything the user can convert against, and the single owner of that answer.
 *
 * It takes two endpoints to say it: `GET /wordlists` returns the lists the user *owns*, and
 * `GET /user/wordlists` returns their collection, which is where a list added from the marketplace
 * (and an official list) is the only place it exists. Reading either one alone drops half the
 * answer — the conversion picker used to read only the collection, so a user's own lists were
 * invisible there while the word lists page showed them. Anything offering a word list to pick
 * reads this, and dispatches both `fetchUserWordlists` and `fetchUserWordlistsCollection`.
 */
export const myWordlistsSelector = createSelector(
  [userWordlistsDataSelector, userWordlistsCollectionDataSelector],
  (owned, collection) => {
    const lists = [
      ...owned.filter(list => list && list.isOwner !== false),
      ...collection.filter(list => list && list.isOwner === false),
    ];

    // The two endpoints can describe the same list, so the owned copy (which carries the words) wins.
    const seen = new Set<string>();
    return lists.filter((list) => {
      if (seen.has(list.id)) {
        return false;
      }
      seen.add(list.id);
      return true;
    });
  }
);

// Error selector
export const wordlistsErrorSelector = createSelector(
  [wordlistsSelector],
  wordlists => wordlists.error
);

// Utility selectors
export const hasUserWordlistsSelector = createSelector(
  [userWordlistsDataSelector],
  wordlists => wordlists.length > 0
);

export const hasMarketplaceWordlistsSelector = createSelector(
  [marketplaceWordlistsDataSelector],
  wordlists => wordlists.length > 0
);

// Filtered/sorted selectors
export const publicUserWordlistsSelector = createSelector(
  [userWordlistsDataSelector],
  wordlists => wordlists.filter(w => w.isPublic)
);

export const privateUserWordlistsSelector = createSelector(
  [userWordlistsDataSelector],
  wordlists => wordlists.filter(w => !w.isPublic)
);

// Sort user wordlists by updated date (most recent first)
export const sortedUserWordlistsSelector = createSelector(
  [userWordlistsDataSelector],
  (wordlists) => {
    return [...wordlists].sort((a, b) => {
      return new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    });
  }
);

// Sort marketplace wordlists by updated date (most recent first)
export const sortedMarketplaceWordlistsSelector = createSelector(
  [marketplaceWordlistsDataSelector],
  (wordlists) => {
    return [...wordlists].sort((a, b) => {
      return new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    });
  }
);

// User collection utility selectors
export const hasUserWordlistsCollectionSelector = createSelector(
  [userWordlistsCollectionDataSelector],
  wordlists => wordlists.length > 0
);

export const sortedUserWordlistsCollectionSelector = createSelector(
  [userWordlistsCollectionDataSelector],
  (wordlists) => {
    return [...wordlists].sort((a, b) => {
      return new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    });
  }
);

// Check if a wordlist is in the user's collection
export const isWordlistInCollectionSelector = (wordlistId: string) =>
  createSelector(
    [userWordlistsCollectionDataSelector],
    collection => collection.some(w => w.id === wordlistId)
  );

// Filter collection by ownership
export const ownedWordlistsInCollectionSelector = createSelector(
  [userWordlistsCollectionDataSelector],
  wordlists => wordlists.filter(w => w.isOwner)
);

export const publicWordlistsInCollectionSelector = createSelector(
  [userWordlistsCollectionDataSelector],
  wordlists => wordlists.filter(w => !w.isOwner)
);
